"use client";
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { BASE_URL } from "../config/apiconfig";
import { useAuth } from "./AuthContext";

const ShipmentContext = createContext();
export const useShipment = () => useContext(ShipmentContext);

// 🔁 Backend → Frontend normalizer
const normalizeShipment = (s) => ({
  id: s?.id ? String(s.id) : "",
  title: s?.title ?? "",
  description: s?.description ?? "",
  amount: Number(s?.amount ?? 0),
  status: s?.status ? s.status.toLowerCase() : "Inactive",
  create_audit_id: s?.create_audit_id ?? null,
  create_audit_time: s?.create_audit_time ?? null,
  update_audit_id: s?.update_audit_id ?? null,
  update_audit_time: s?.update_audit_time ?? null,
  delete_audit_id: s?.delete_audit_id ?? null,
  delete_audit_time: s?.delete_audit_time ?? null,
});

export const ShipmentProvider = ({ children }) => {
  const { token, logout } = useAuth();

  const [shipmentMethods, setShipmentMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const SHIPMENT_ENDPOINT = `${BASE_URL}/shipping-methods`;
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

    // 🔹 FETCH
    const fetchShipmentMethods = useCallback(async () => {
        if (!token) return;

        setLoading(true);
        setError(null);

        try {
        const res = await fetch(SHIPMENT_ENDPOINT, { headers });
        if (!res.ok) throw new Error("API failed");

        const json = await res.json();
        let list = [];

        if (Array.isArray(json)) list = json;
        else if (Array.isArray(json.data)) list = json.data;
        else if (json && typeof json === "object" && json.id) list = [json];

        setShipmentMethods(list.map(normalizeShipment));
        } catch (err) {
        setShipmentMethods([]);
        setError("Failed to load shipment methods");
        } finally {
        setLoading(false);
        }
    }, [token]);

    // 🔹 ADD
    const addShipmentMethod = async (payload) => {
        try {
            const create_audit_id = localStorage.getItem("admin_id");

            const res = await fetch(SHIPMENT_ENDPOINT, {
            method: "POST",
            headers,
            body: JSON.stringify({
                title: payload.title,
                description: payload.description,
                amount: payload.amount,
                status: payload.status === "Active" ? "Active" : "Inactive",
                create_audit_id,
            }),
            });

            if (!res.ok) throw new Error("Failed to add shipment method");

            const json = await res.json();

            // ✅ Refetch the shipment methods after successful add
            await fetchShipmentMethods();
            
        } catch (err) {
            console.error("Add shipment error:", err);
        }
    };

    // 🔹 UPDATE
    const updateShipmentMethod = async (payload) => {
        try {
                const update_audit_id = localStorage.getItem("admin_id");

                const res = await fetch(`${SHIPMENT_ENDPOINT}/${payload.id}`, {
                method: "PUT",
                headers,
                body: JSON.stringify({
                    title: payload.title,
                    description: payload.description,
                    amount: payload.amount,
                    status: payload.status === "Active" ? "Active" : "Inactive",
                    update_audit_id,
                }),
            });

            if (!res.ok) throw new Error("Failed to update shipment method");

            await fetchShipmentMethods(); // ✅ Refresh after update

        } catch (err) {
            console.error("Update shipment error:", err);
        }
    };



    // 🔹 DELETE
    const deleteShipmentMethod = async (id) => {
        try {
            const delete_audit_id = localStorage.getItem("admin_id");

            const res = await fetch(`${SHIPMENT_ENDPOINT}/${id}`, {
            method: "DELETE",
            headers,
            body: JSON.stringify({ delete_audit_id }), // ✅ pass audit ID
            });

            if (!res.ok) throw new Error("Failed to delete shipment method");

            // ✅ Refetch the latest shipment methods after delete
            await fetchShipmentMethods();
            
        } catch (err) {
            console.error("Delete shipment error:", err);
        }
    };


    // 🔹 TOGGLE STATUS
    const toggleShipmentStatus = async (item) => {
        try {
            const update_audit_id = localStorage.getItem("admin_id");

            await updateShipmentMethod({
            ...item,
            status: item.status === "Active" ? "Inactive" : "Active",
            update_audit_id, // ✅ pass audit ID
            });

            // ✅ No extra fetch needed because updateShipmentMethod already refetches
        } catch (err) {
            console.error("Toggle status error:", err);
        }
    };


  useEffect(() => {
    fetchShipmentMethods();
  }, [fetchShipmentMethods]);

  return (
    <ShipmentContext.Provider
      value={{
        shipmentMethods,
        loading,
        error,
        addShipmentMethod,
        updateShipmentMethod,
        deleteShipmentMethod,
        toggleShipmentStatus,
        refreshShipmentMethods: fetchShipmentMethods,
      }}
    >
      {children}
    </ShipmentContext.Provider>
  );
};
